import { useEffect, useState } from 'react';
import type { Meeting } from '@shared/contract';
import { navigate } from '../App';
import { api } from '../lib/api';
import { formatDate, formatDuration, STATUS_LABELS } from '../lib/format';
import { uploadQueue, type QueueState } from '../lib/queue';
import { startOnOpen } from './MeetingView';

export function MeetingList() {
  const [meetings, setMeetings] = useState<Meeting[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [queue, setQueue] = useState<QueueState>(uploadQueue.getState());

  const load = () => api.listMeetings().then(setMeetings, (e) => setError(e.message));
  useEffect(() => {
    void load();
    return uploadQueue.subscribe(setQueue);
  }, []);

  async function create() {
    try {
      const m = await api.createMeeting(title.trim() || undefined);
      startOnOpen(m.id);
      navigate(`/m/${m.id}`);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function remove(m: Meeting) {
    if (!confirm(`Radera "${m.title}" med ljud, transkription och sammanfattningar? Det går inte att ångra.`)) return;
    await api.deleteMeeting(m.id).catch((e) => setError(e.message));
    void load();
  }

  return (
    <div className="page">
      <header className="page-head">
        <h1>Möten</h1>
        <p className="lede">Spela in, transkribera och sammanfatta dina möten.</p>
      </header>
      <section className="card new-meeting">
        <h2>Nytt möte</h2>
        <div className="row">
          <input
            placeholder="Titel (valfritt)"
            value={title}
            maxLength={200}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && create()}
          />
          <button className="btn primary" onClick={create}>
            Starta nytt möte
          </button>
        </div>
        <p className="muted small">Inspelningen startar direkt när du klickar. Informera alla deltagare först och spela bara in när det är tillåtet.</p>
      </section>

      {queue.pending > 0 && (
        <div className="alert warn">
          {queue.pending} ljudsegment från tidigare inspelning väntar på uppladdning.{' '}
          {queue.online ? 'De skickas nu.' : 'De skickas när anslutningen är tillbaka.'}
        </div>
      )}
      {error && <div className="alert error">{error}</div>}

      <section className="card">
        <h2>Tidigare möten</h2>
        {meetings === null ? (
          <p className="muted">Laddar…</p>
        ) : meetings.length === 0 ? (
          <p className="muted">Inga möten ännu.</p>
        ) : (
          <ul className="meeting-list">
            {meetings.map((m) => (
              <li key={m.id}>
                <a href={`#/m/${m.id}`} className="meeting-link">
                  <span className="meeting-title">{m.title}</span>
                  <span className="muted small">
                    {formatDate(m.started_at ?? m.created_at)} · {formatDuration(m.duration_ms)}
                  </span>
                </a>
                <span className={`pill status-${m.status}`}>{STATUS_LABELS[m.status]}</span>
                <button className="btn ghost small" onClick={() => remove(m)} aria-label={`Radera ${m.title}`}>
                  Radera
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
